import { useState, useEffect, useRef } from 'react';

/**
 * sep 2026 · Reyes: "he subido el vídeo pero no sale en ningún sitio,
 * podríamos usarlos de portada?" — el admin ya podía subir un vídeo de
 * portada desde el panel de Embajadores, pero ningún componente público
 * lo leía. Esto lo usa como portada "viva" (autoplay, mute, loop) allí
 * donde antes solo se mostraba la foto — con fallback automático a la
 * foto si no hay vídeo o si el vídeo falla al cargar.
 *
 * `onUnavailable`: se dispara una única vez si ni el vídeo ni la foto
 * pueden mostrarse, para que el padre pueda ocultar el contenedor entero
 * en vez de dejar una caja gris vacía con el degradado encima.
 */
export const RouteCoverMedia = ({ videoUrl, coverUrl, alt = '', className, imgClassName, onUnavailable }) => {
  const [videoFailed, setVideoFailed] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const [near, setNear] = useState(false);
  const holder = useRef(null);

  // El vídeo solo se descarga cuando la tarjeta está cerca de la pantalla (antes, la foto).
  useEffect(() => {
    if (!videoUrl || near) return undefined;
    const el = holder.current;
    if (!el || typeof IntersectionObserver === 'undefined') { setNear(true); return undefined; }
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setNear(true); io.disconnect(); } }, { rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  }, [videoUrl, near]);

  const showVideo = Boolean(videoUrl) && !videoFailed;
  const showImage = !showVideo && Boolean(coverUrl) && !imageFailed;

  useEffect(() => {
    if (!showVideo && !showImage) onUnavailable?.();
  }, [showVideo, showImage]);

  if (showVideo && !near) {
    return coverUrl
      ? <img ref={holder} src={coverUrl} alt={alt} className={imgClassName || className} loading="lazy" data-testid="route-cover-photo" />
      : <span ref={holder} className={imgClassName || className} />;
  }
  if (showVideo) {
    return (
      <video
        src={videoUrl}
        poster={coverUrl || undefined}
        className={imgClassName || className}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        onError={() => setVideoFailed(true)}
        data-testid="route-cover-video"
      />
    );
  }
  if (showImage) {
    return (
      <img
        src={coverUrl}
        alt={alt}
        className={imgClassName || className}
        loading="lazy"
        onError={(e) => {
          const s = e.currentTarget.getAttribute('src') || '';
          if (/[?&]w=\d+/.test(s)) e.currentTarget.src = s.replace(/([?&])w=\d+&?/, '$1').replace(/[?&]$/, '');
          else setImageFailed(true);
        }}
        data-testid="route-cover-photo"
      />
    );
  }
  return null;
};

export default RouteCoverMedia;
